import { bbox } from '@turf/turf';
import type { BBox, Feature } from 'geojson';

import {
  calculateAreaSizeInHa,
  intersectionFeature,
  isPolygonalGeometry,
  unionPolygonFeatures,
  type PolygonFeature,
} from '@/common/helpers/geo.helper';

/**
 * Fraction of a child zone's area its aggregate feature must already cover to be
 * left alone. Below this the aggregate is stale and gets rebuilt from the zone
 * geometry — a 1% tolerance absorbs the backend's simplification of the outline
 * without letting a placeholder through. Unrelated to the raster-coverage
 * threshold of the same value: this compares two versions of one boundary.
 */
export const AGGREGATE_COVERAGE_RATIO = 0.99;

/** What is known about one aggregate feature; null for one without an area. */
interface IndexedFeature {
  feature: PolygonFeature;
  key: string;
  box: BBox;
}

const geometryKey = (feature: PolygonFeature): string =>
  `${feature.geometry.type}:${JSON.stringify(feature.geometry.coordinates)}`;

/**
 * Always recomputed: turf returns a feature's stored `bbox` member as-is, and a
 * stale or 3-D one from the backend would drop a covering feature from `near`,
 * so a present child reads as missing and is added twice (doubling its hectares).
 */
const boxOf = (feature: PolygonFeature): BBox => bbox(feature, { recompute: true });

const boxesOverlap = (a: BBox, b: BBox): boolean => a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3];

/**
 * Answers "does a multizone's aggregate FeatureCollection already cover this
 * child's ground?", for `reconcileMultizoneFeatures`.
 *
 * The obvious test, intersecting the child with the union of the WHOLE
 * aggregate, cost ~5 s of main thread per 50-70-zone project on prod (Mangrove,
 * Brújula Verde; Projects Paraguay 16 s), which froze the home page while its
 * project cards loaded. Two cheaper steps give the same answer:
 *
 * 1. The aggregate holds the child's geometry verbatim: covered. This is the
 *    healthy case for most children (48 of Mangrove's 67).
 * 2. Otherwise intersect only against the aggregate features whose bounding box
 *    overlaps the child's. A feature whose box misses the child's box cannot
 *    contribute area to the intersection, so the local union answers exactly
 *    what the global one did.
 *
 * Checked against the full-union test on eight prod projects: the same children
 * came out uncovered in every one.
 *
 * Edits to the collection go through `replace` / `append`, which keep the index
 * in step. A replacement drops the old shape, where a union folded in earlier
 * could not subtract it.
 */
export class AggregateCoverage {
  private readonly entries: (IndexedFeature | null)[];
  private readonly keyCounts = new Map<string, number>();

  public constructor(private readonly features: Feature[]) {
    this.entries = features.map((feature) => this.index(feature));
  }

  public covers(zoneFeature: PolygonFeature, zoneAreaHa: number): boolean {
    if (this.keyCounts.has(geometryKey(zoneFeature))) {
      return true;
    }

    const zoneBox = boxOf(zoneFeature);
    const near = this.entries
      .filter((entry): entry is IndexedFeature => entry !== null && boxesOverlap(zoneBox, entry.box))
      .map((entry) => entry.feature);
    if (!near.length) {
      return false;
    }

    const local = unionPolygonFeatures(near);
    const covered = local ? intersectionFeature(zoneFeature, local) : null;
    return !!covered && calculateAreaSizeInHa(covered) >= AGGREGATE_COVERAGE_RATIO * zoneAreaHa;
  }

  /** Overwrite `features[index]`. */
  public replace(index: number, feature: Feature): void {
    const old = this.entries[index];
    if (old) {
      const count = (this.keyCounts.get(old.key) ?? 1) - 1;
      if (count > 0) {
        this.keyCounts.set(old.key, count);
      } else {
        this.keyCounts.delete(old.key);
      }
    }
    this.features[index] = feature;
    this.entries[index] = this.index(feature);
  }

  /** Push `feature` onto `features`. */
  public append(feature: Feature): void {
    this.features.push(feature);
    this.entries.push(this.index(feature));
  }

  private index(feature: Feature | undefined): IndexedFeature | null {
    if (!feature || !isPolygonalGeometry(feature.geometry)) {
      return null;
    }
    const polygon = feature as PolygonFeature;
    const key = geometryKey(polygon);
    this.keyCounts.set(key, (this.keyCounts.get(key) ?? 0) + 1);
    return { feature: polygon, key, box: boxOf(polygon) };
  }
}
